/* istanbul ignore file */
import React, { useEffect, useMemo, useState } from 'react';
import { ACCOUNT_TYPES_MAP } from '@linearb/linjs-common';
import { useDispatch, useSelector } from 'react-redux';
import { syncTeamsTreeSelector } from 'state/selectors/teams';
import { getAccountsForUsersTableWithPendingInvitations } from 'state/selectors/account';
import { useHistory, useLocation, useParams } from 'react-router';
import keyBy from 'lodash/keyBy';
import AppRouter from 'common/AppRouter';
import {
  accountScopesToSelectedTeamIds,
  getSelectedAccounts,
  selectedTeamIdsToAccountScopes,
  isAllTeamsSelected,
  isAllTeamsGroupSelected
} from './Permissions.service';
import {
  updateAccountPermissions,
  updateAccountsBulkPermissions
} from 'common/services/account.service';
import { getAccounts, toggleSnackBar } from 'state/callers';
import { allTeams } from 'connected-components/TopBar/TopBar.service';
import SnackBarMessage from 'components/Message';
import { updateInvitationPermission } from 'common/services/invitations.service';
import { getInvitation } from 'state/callers/invitations';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import _ from 'lodash';
import { getAllTeamsGroup, getTreeWhenAllTeamsGroup } from 'common/utils/utils';
import useRoleBasedModules from './RoleBasedModules/useRoleBasedModules';

const UsePermissionsData = ({ isMultiple }) => {
  const [accountTypeId, setAccountTypeId] = useState(
    isMultiple ? ACCOUNT_TYPES_MAP.Primary : ACCOUNT_TYPES_MAP.Viewer
  );
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const dispatch = useDispatch();
  let treeTeams = useSelector(syncTeamsTreeSelector);
  const accounts = useSelector(getAccountsForUsersTableWithPendingInvitations);
  const history = useHistory();
  const { id = '' } = useParams();
  const location = useLocation();
  const accountsById = useMemo(() => keyBy(accounts, 'id'), [accounts]);
  const allTeamsGroup = getAllTeamsGroup(treeTeams);
  const isAllTeamsGroup = !_.isEmpty(allTeamsGroup);
  const allTeamsGroupId = allTeamsGroup?.id;

  // when we are in all teams group mode
  if (!!treeTeams.length && isAllTeamsGroup) {
    treeTeams = getTreeWhenAllTeamsGroup(treeTeams);
  }

  const selectedAccountIds = isMultiple
    ? location.state?.accountIds || []
    : [parseInt(id)];

  if (!selectedAccountIds.length) {
    history.push({
      pathname: AppRouter.USERS.path
    });
  }
  const selectedAccounts = getSelectedAccounts({
    selectedAccountIds,
    accountsById
  });

  // for a case of 1 account
  const accountScopes = _.get(selectedAccounts[0], 'account_scopes', []) || [];
  const isInvitation = _.get(selectedAccounts[0], 'isPending', false);

  useEffect(() => {
    const hasSelectedAccount =
      selectedAccounts.length === 1 && !!selectedAccounts[0]?.id;

    if (accounts.length <= 1) {
      setIsLoading(true);
      dispatch(getAccounts());
      dispatch(getInvitation());
    } else if (hasSelectedAccount) {
      const accountType = _.get(
        selectedAccounts[0],
        isInvitation ? 'account_type_id' : 'type_id'
      );
      setAccountTypeId(accountType);
      setSelectedTeamIds(
        accountScopesToSelectedTeamIds(accountScopes, isAllTeamsGroup)
      );
      setIsLoading(false);
    } else if (selectedAccountIds.length > 1) {
      setIsLoading(false);
    }
  }, [accounts.length]); // to make sure get accounts was called

  const isAdmin = accountTypeId === ACCOUNT_TYPES_MAP.Primary;

  const roleBasedModules = useRoleBasedModules({
    userConfig: { isAdmin, accountTypeId, selectedAccounts }
  });

  useEffect(() => {
    if (isAdmin) {
      if (isAllTeamsGroup) {
        setSelectedTeamIds([allTeamsGroupId]);
      } else {
        // TO DO: when all teams group is GA delete this
        setSelectedTeamIds([allTeams.id]);
      }
    }
  }, [isAdmin]);

  const onTeamDropdownChange = selectedTeams =>
    setSelectedTeamIds(
      selectedTeams[0] === allTeams.id
        ? [allTeams.id]
        : selectedTeams.map(({ id }) => id)
    );

  const onSaveAccount = async () => {
    const { isPending, id: accountId } = selectedAccounts[0] || {};
    const updateMethod = isPending
      ? updateInvitationPermission
      : updateAccountPermissions;
    try {
      setIsLoadingUpdate(true);

      await updateMethod(accountId, {
        type_id: accountTypeId,
        scopes: selectedTeamIdsToAccountScopes(
          selectedTeamIds,
          allTeamsGroupId
        ),
        permissions: roleBasedModules.userPermissionDomainIds
      });
      if (
        !isPending &&
        (!isAllTeamsSelected(selectedTeamIds) ||
          !isAllTeamsGroupSelected(selectedTeamIds, allTeamsGroupId))
      ) {
        // Amplitude when updating account with RBAC team scopes
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.PERMISSIONS,
          click_type: TRACKING.CLICK.RBAC.SETTINGS.EDIT_USER_PERMISSIONS,
          setting_value: selectedTeamIds,
          setting_name: TRACKING.CLICK.RBAC.SETTINGS.SAVE_USER_TEAMS_SCOPE,
          event: TRACKING.EVENTS.CLICK
        });
      }

      setIsLoadingUpdate(false);
      dispatch(
        toggleSnackBar({
          message: <SnackBarMessage message='Update permissions succeed' />,
          variant: 'success'
        })
      );
      history.goBack();
    } catch (e) {
      setIsLoadingUpdate(false);
      dispatch(
        toggleSnackBar({
          message: (
            <SnackBarMessage message='an error has occurred, please try again.' />
          ),
          variant: 'error'
        })
      );
    }
  };

  const onSaveBulkAccounts = async () => {
    try {
      setIsLoadingUpdate(true);
      await updateAccountsBulkPermissions({
        account_ids: selectedAccountIds,
        type_id: accountTypeId,
        scopes: selectedTeamIdsToAccountScopes(selectedTeamIds, allTeamsGroupId)
      });
      setIsLoadingUpdate(false);
      dispatch(
        toggleSnackBar({
          message: <SnackBarMessage message='Update permissions succeed' />,
          variant: 'success'
        })
      );
      history.goBack();
    } catch (e) {
      setIsLoadingUpdate(false);
      dispatch(
        toggleSnackBar({
          message: (
            <SnackBarMessage message='an error has occurred, please try again.' />
          ),
          variant: 'error'
        })
      );
    }
  };

  return {
    selectedAccounts,
    accountTypeId,
    setAccountTypeId,
    isAdmin,
    selectedTeamIds,
    setSelectedTeamIds,
    onTeamDropdownChange,
    isLoading,
    setIsLoading,
    onSaveAccount,
    treeTeams,
    isLoadingUpdate,
    setIsLoadingUpdate,
    onSaveBulkAccounts,
    isAllTeamsGroup,
    allTeamsGroupId,
    roleBasedModules
  };
};

export default UsePermissionsData;
